<!-- src/components/forms/completed/ClientFeedbackCompleted.vue -->
<template>
  <div class="space-y-12 animate-fadeIn text-left font-sans">
    
    <!-- 1. Report Metadata Banner -->
    <div class="flex flex-wrap items-center justify-between gap-6 p-8 bg-slate-900 rounded-[40px] text-white shadow-2xl relative overflow-hidden">
      <div class="absolute top-0 right-0 p-10 opacity-5">
        <ShieldCheckIcon class="h-32 w-32" />
      </div>
      <div class="relative z-10 space-y-2">
        <p class="text-[10px] font-black uppercase tracking-[0.2em] text-[#20FCF1]">Client Feedback Record</p>
        <h2 class="text-2xl font-black uppercase tracking-tight">{{ submissionData.projectName }}</h2>
        <div class="flex items-center gap-4 text-slate-400 text-xs font-bold">
          <span>PROJECT REF: {{ submissionData.projectRef }}</span>
          <span class="w-1.5 h-1.5 rounded-full bg-slate-700"></span>
          <span>REG REF: {{ submissionData.humanReadableId || submissionData.referenceNumber || 'SS-CFQ' }}</span>
        </div>
      </div>
      <div class="relative z-10 flex flex-col items-end gap-2">
        <span class="bg-[#20FCF1] text-[#0A1630] px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg">Questionnaire Complete</span>
        <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{{ formatDate(submissionData.submittedAt) }}</p>
      </div>
    </div>

    <!-- 2. Project Details Grid -->
    <section>
      <div class="flex items-center gap-3 mb-6">
        <div class="bg-[#20FCF1] p-2 rounded-lg shadow-sm">
          <IdentificationIcon class="h-5 w-5 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">1. Project Details & Respondent Information</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 bg-slate-50 p-8 rounded-[32px] border border-slate-100 shadow-inner">
        <div class="StatCard bg-white">
          <span class="label">Client / Project</span>
          <span class="value">{{ submissionData.projectName }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">Project Ref (Job No.)</span>
          <span class="value">{{ submissionData.projectRef }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">Date of Feedback</span>
          <span class="value">{{ submissionData.feedbackDate }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">Respondent Name</span>
          <span class="value">{{ submissionData.respondentName }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">Position / Role</span>
          <span class="value">{{ submissionData.respondentRole }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">Department / Source</span>
          <span class="value">{{ submissionData.departmentSource || 'N/A' }}</span>
        </div>
      </div>
    </section>

    <!-- 3. Feedback Type Badges -->
    <section class="space-y-4">
      <div class="flex items-center gap-3 mb-4">
        <div class="bg-[#20FCF1] p-2 rounded-lg shadow-sm">
          <TagIcon class="h-5 w-5 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">2. Feedback Type Classification</h3>
      </div>
      <div class="flex flex-wrap gap-2">
        <span 
          v-for="ftype in (submissionData.feedbackTypes || [])" 
          :key="ftype"
          class="px-4 py-2 bg-slate-100 text-[#0A1630] rounded-xl text-[10px] font-black uppercase tracking-wider border border-slate-200 shadow-sm"
        >
          ✓ {{ ftype }}
        </span>
        <span v-if="!submissionData.feedbackTypes?.length" class="text-xs text-slate-400 italic">No specific classification tagged.</span>
      </div>
    </section>

    <!-- 4. Service Ratings Breakdown (8 Questions) -->
    <section class="space-y-6">
      <div class="flex items-center gap-3 mb-6">
        <div class="bg-[#20FCF1] p-2 rounded-lg shadow-sm">
          <StarIcon class="h-5 w-5 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">3. Service Ratings Breakdown (1 to 5 Scale)</h3>
      </div>

      <div class="space-y-4">
        <div 
          v-for="(question, qIdx) in serviceQuestions" 
          :key="qIdx"
          class="p-6 bg-white border border-slate-100 rounded-3xl shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4"
        >
          <p class="text-xs font-bold text-slate-700 leading-relaxed flex-1">
            <span class="text-[#0A1630] font-black mr-2">{{ qIdx + 1 }}.</span> {{ question }}
          </p>
          <div class="flex items-center gap-2 shrink-0">
            <span class="px-4 py-2 bg-[#0A1630] text-[#20FCF1] rounded-xl text-xs font-black uppercase tracking-widest shadow-sm">
              Score: {{ submissionData.serviceRatings?.[qIdx] || 'N/A' }} / 5
            </span>
          </div>
        </div>
      </div>
    </section>

    <!-- 5. Overall Satisfaction & Recommendation -->
    <section class="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div class="p-8 bg-white border border-slate-200 rounded-[32px] shadow-sm space-y-3">
        <span class="text-[9px] font-black text-slate-400 uppercase tracking-widest block">Overall Satisfaction Score</span>
        <div class="flex items-center gap-3">
          <StarIcon class="h-8 w-8 text-amber-400 fill-amber-400" />
          <span class="text-3xl font-black text-[#0A1630] tracking-tighter">{{ submissionData.overallSatisfaction || '0' }} <span class="text-xs text-slate-400 font-normal">/ 5</span></span>
        </div>
        <p class="text-xs font-bold uppercase text-slate-600 pt-2">
          {{ submissionData.overallSatisfaction === 5 ? 'Exceeds expectations / Excellent' : submissionData.overallSatisfaction === 4 ? 'Meets expectations / Good' : submissionData.overallSatisfaction === 3 ? 'Average' : submissionData.overallSatisfaction === 2 ? 'Below expectations' : 'Unacceptable' }}
        </p>
      </div>

      <div class="p-8 bg-white border border-slate-200 rounded-[32px] shadow-sm space-y-3">
        <span class="text-[9px] font-black text-slate-400 uppercase tracking-widest block">Recommendation Likelihood (0 to 5)</span>
        <div class="flex items-center gap-3">
          <span class="text-3xl font-black text-[#0A1630] tracking-tighter">{{ submissionData.recommendationLikelihood !== null ? submissionData.recommendationLikelihood : 'N/A' }} <span class="text-xs text-slate-400 font-normal">/ 5</span></span>
        </div>
        <p class="text-xs font-bold uppercase text-slate-600 pt-2">
          {{ submissionData.recommendationLikelihood >= 4 ? 'Extremely Likely' : submissionData.recommendationLikelihood >= 2 ? 'Neutral / Likely' : 'Not at all likely' }}
        </p>
      </div>
    </section>

    <!-- 6. Comments & Suggestions -->
    <section v-if="submissionData.comments" class="bg-slate-50 p-8 rounded-[32px] border border-slate-100 shadow-inner space-y-3">
      <span class="text-[9px] font-black text-slate-400 uppercase tracking-widest block">4. Comments & Suggestions</span>
      <p class="text-xs font-medium text-slate-700 leading-relaxed italic bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        "{{ submissionData.comments }}"
      </p>
    </section>

    <!-- 7. Completion & Signature -->
    <section class="border-t pt-12">
      <div class="flex flex-col md:flex-row justify-between items-end gap-12">
        <div class="space-y-6 max-w-lg">
          <div class="flex items-center gap-2">
            <CheckCircleIcon class="h-5 w-5 text-[#20FCF1]" />
            <p class="text-[10px] font-black uppercase tracking-widest text-[#0A1630]">Official Client Sign-Off</p>
          </div>
          <div class="p-8 bg-slate-900 rounded-[32px] border border-slate-800 text-slate-300">
            <p class="text-[10px] font-black text-[#20FCF1] uppercase tracking-widest mb-2">Questionnaire Attestation</p>
            <p class="text-xs font-medium italic leading-relaxed">
              "This questionnaire has been completed by the client representative to evaluate performance and quality standards."
            </p>
          </div>
        </div>

        <div v-if="submissionData.signatureDataUrl" class="bg-white p-8 rounded-[40px] border border-slate-200 shadow-2xl flex flex-col items-center min-w-[280px]">
          <p class="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-6">Client Signature Seal</p>
          <img :src="submissionData.signatureDataUrl" alt="Client Seal" class="max-h-24 object-contain mb-6" />
          <div class="w-full h-px bg-slate-100 mb-6"></div>
          <div class="flex items-center gap-2">
            <CheckBadgeIcon class="h-5 w-5 text-[#20FCF1]" />
            <span class="text-[10px] font-black uppercase text-[#0A1630] tracking-[0.1em]">Verification Passed</span>
          </div>
          <p class="text-[10px] font-bold text-slate-400 mt-3">Completed: {{ submissionData.completionDate || formatDate(submissionData.submittedAt) }}</p>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { 
  IdentificationIcon, ShieldCheckIcon, TagIcon, StarIcon, 
  CheckCircleIcon, CheckBadgeIcon 
} from '@heroicons/vue/24/outline';

defineProps({
  submissionData: { type: Object, required: true },
  formatDate: { type: Function, required: true }
});

const serviceQuestions = [
  "How well did the labour team understand the scope of works, method statements, and site-specific safety requirements before starting?",
  "How would you rate the skill, experience, and overall competence of the operatives supplied to this project?",
  "How would you rate the quality, accuracy, and finish of the work completed on site?",
  "How well did the team plan, manage, and deliver the works against the agreed programme and key dates?",
  "How easy was it to communicate with the team on site (including spoken English and understanding of instructions)?",
  "How well did the team work safely on site, follow RAMS/PPE rules, and keep the work area tidy and controlled?",
  "How reliably did the team follow instructions, complete allocated tasks, and cooperate with your staff and other contractors?",
  "How satisfied are you with the supervisor/team leader on site, including their competence, attitude, organisation, and ability to keep the job moving?"
];
</script>

<style scoped>
.StatCard { @apply flex flex-col p-6 rounded-[28px] border border-slate-100 shadow-sm; }
.StatCard .label { @apply text-[9px] font-black uppercase text-slate-400 tracking-widest mb-1; }
.StatCard .value { @apply text-sm font-black text-[#0A1630] truncate; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>
